import { useState, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CartContext } from '@/context/CartContext';
import type { Product } from '@/mocks/products';

interface ProductInfoProps {
  product: Product;
}

const WHATSAPP_NUMBER = '919686543847';

export default function ProductInfo({ product }: ProductInfoProps) {
  const { addItem } = useContext(CartContext);
  const [added, setAdded] = useState(false);

  // Variant selection
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    if (product.variants) {
      product.variants.forEach((v) => {
        initial[v.label] = v.options[0];
      });
    }
    return initial;
  });

  // Quantity
  const [quantity, setQuantity] = useState(1);

  const discount =
    product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : 0;

  const variantString = product.variants
    ? product.variants.map((v) => `${v.label}: ${selectedVariants[v.label]}`).join(', ')
    : undefined;

  const handleAddToCart = useCallback(() => {
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0],
      quantity,
      variant: variantString,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  }, [product, quantity, variantString, addItem]);

  const handleBuyWhatsApp = useCallback(() => {
    const lines = [
      'Hi Joviaa! I\'d like to order:',
      '',
      `${product.name} x${quantity} — ₹${product.price * quantity}`,
    ];
    if (variantString) {
      lines.splice(2, 0, `Variant: ${variantString}`);
    }
    lines.push('');
    lines.push('Please confirm availability and final pricing.');

    const message = encodeURIComponent(lines.join('\n'));
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [product, quantity, variantString]);

  const incrementQty = () => setQuantity((q) => Math.min(q + 1, 10));
  const decrementQty = () => setQuantity((q) => Math.max(q - 1, 1));

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      {/* Breadcrumb */}
      <nav className="flex items-center flex-wrap gap-1.5 text-xs font-label">
        <Link to="/" className="text-foreground-400 hover:text-accent-500 transition-colors">
          Home
        </Link>
        <span className="text-foreground-300">
          <i className="ri-arrow-right-s-line text-[10px]"></i>
        </span>
        <Link to="/shop" className="text-foreground-400 hover:text-accent-500 transition-colors">
          Shop
        </Link>
        <span className="text-foreground-300">
          <i className="ri-arrow-right-s-line text-[10px]"></i>
        </span>
        <Link
          to={`/shop?occasion=${encodeURIComponent(product.occasion)}`}
          className="text-foreground-400 hover:text-accent-500 transition-colors"
        >
          {product.occasion}
        </Link>
        <span className="text-foreground-300">
          <i className="ri-arrow-right-s-line text-[10px]"></i>
        </span>
        <span className="text-foreground-700 font-medium truncate max-w-[160px]">
          {product.name}
        </span>
      </nav>

      {/* Product name */}
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-primary-500 leading-tight">
        {product.name}
      </h1>

      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1 text-[10px] font-label font-semibold uppercase tracking-wider bg-accent-100 text-accent-700 rounded-full">
          {product.style}
        </span>
        <span className="px-3 py-1 text-[10px] font-label font-semibold uppercase tracking-wider bg-accent-100 text-accent-700 rounded-full">
          {product.occasion}
        </span>
        {product.isLimited && (
          <span className="px-3 py-1 text-[10px] font-label font-semibold uppercase tracking-wider bg-accent-700/15 text-accent-700 rounded-full">
            Limited Edition
          </span>
        )}
        {product.isSet && (
          <span className="px-3 py-1 text-[10px] font-label font-semibold uppercase tracking-wider bg-secondary-300 text-secondary-800 rounded-full">
            Set of {product.name.includes('3') ? '3' : '2'}
          </span>
        )}
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-3">
        <span className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
          ₹{product.price}
        </span>
        {product.mrp > product.price && (
          <>
            <span className="text-base md:text-lg text-foreground-400 line-through font-label">
              ₹{product.mrp}
            </span>
            <span className="text-sm font-label font-semibold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">
              {discount}% off
            </span>
          </>
        )}
      </div>

      {/* Low stock */}
      {product.isLowStock && (
        <p className="text-sm font-label font-medium text-accent-500 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500 inline-block"></span>
          Only a few left — order soon
        </p>
      )}

      {/* Description */}
      <p className="text-sm md:text-base text-foreground-600 leading-relaxed font-label">
        {product.description}
      </p>

      {/* Variants */}
      {product.variants && product.variants.length > 0 && (
        <div className="flex flex-col gap-4">
          {product.variants.map((variant) => (
            <div key={variant.label}>
              <p className="text-xs font-label font-semibold text-foreground-700 uppercase tracking-wider mb-2">
                {variant.label}
              </p>
              <div className="flex flex-wrap gap-2">
                {variant.options.map((option) => {
                  const isSelected = selectedVariants[variant.label] === option;
                  return (
                    <button
                      key={option}
                      onClick={() =>
                        setSelectedVariants((prev) => ({
                          ...prev,
                          [variant.label]: option,
                        }))
                      }
                      className={`px-4 py-2 rounded-lg text-xs font-label font-medium border transition-all cursor-pointer whitespace-nowrap ${
                        isSelected
                          ? 'border-accent-500 bg-accent-50 text-accent-700'
                          : 'border-background-300 bg-background-50 text-foreground-600 hover:border-accent-300'
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quantity + CTAs */}
      <div className="flex flex-col gap-4">
        {/* Quantity stepper */}
        <div className="flex items-center gap-1">
          <span className="text-xs font-label font-semibold text-foreground-700 uppercase tracking-wider mr-3">
            Quantity
          </span>
          <div className="flex items-center border border-background-300 rounded-lg overflow-hidden">
            <button
              onClick={decrementQty}
              disabled={quantity <= 1}
              className="w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-subtract-line"></i>
              </div>
            </button>
            <span className="w-10 h-9 flex items-center justify-center text-sm font-label font-semibold text-foreground-800 border-x border-background-300">
              {quantity}
            </span>
            <button
              onClick={incrementQty}
              disabled={quantity >= 10}
              className="w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </div>
            </button>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAddToCart}
            className={`flex-1 py-3.5 rounded-xl font-label font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
              added
                ? 'bg-accent-500 text-primary-800'
                : 'bg-accent-500 text-primary-800 hover:bg-accent-400'
            }`}
          >
            {added ? 'Added to Cart ✓' : 'Add to Cart'}
          </button>
          <button
            onClick={handleBuyWhatsApp}
            className="flex-1 py-3.5 rounded-xl font-label font-semibold text-sm border-2 border-accent-500 text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-whatsapp-fill text-base"></i>
            </div>
            Buy on WhatsApp
          </button>
        </div>
      </div>

      {/* Trust icons */}
      <div className="grid grid-cols-2 gap-3 pt-4 border-t border-background-200/70">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-star-line text-accent-600 text-sm"></i>
            </div>
          </div>
          <span className="text-xs font-label font-medium text-foreground-600">PVD Gold Plated</span>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-drop-line text-accent-600 text-sm"></i>
            </div>
          </div>
          <span className="text-xs font-label font-medium text-foreground-600">Sweat &amp; Water Resistant</span>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-heart-pulse-line text-accent-600 text-sm"></i>
            </div>
          </div>
          <span className="text-xs font-label font-medium text-foreground-600">Nickel-Free</span>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-go-back-line text-accent-600 text-sm"></i>
            </div>
          </div>
          <span className="text-xs font-label font-medium text-foreground-600">7-Day Exchange</span>
        </div>
      </div>
    </div>
  );
}